<template> 
  <div id="map-wrapper" class="fill">
    <div id="map-loader" v-if="loading"> <AppLoader /> </div>
    <div id="map" class="map no-print" ref="map"></div>
    <div v-if="route.live && liveState && liveState.started" id="map-live" class="card card-small card-glass flex flex-f-c">
      <div class="estimate" style="margin-right: .5rem"></div>
      <span class="text-small">Estimeret lokation</span>
    </div>
    <div id="map-controls" class="card no-print">
      <AppButton icon="add" @click="zoom(1)" />
      <AppButton icon="remove" @click="zoom(-1)" />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, watch, computed, onMounted, PropType, onUnmounted } from 'vue'
import { store } from '@/store'
import { Route, RouteLiveState } from '@/api/tripply/models'

export default defineComponent({
  props: {
    route: {
      type: Object as PropType<Route>,
      required: true
    },
    liveState: {
      type: Object as PropType<RouteLiveState>,
      required: false
    }
  },
  name: 'RouteMap',
  setup (props) {
    const loading = store.map.get(state => state.loading)

    onMounted(() => {      
      store.map.createMap('map', props.route)
    })
    
    const theme = computed(() => store.global.state.theme)

    watch(theme, async (theme) => {
      await store.map.setTheme(theme)
      store.map.updateGeometry(props.route.geometry, props.liveState)
    })

    watch(() => props.route.geometry, () => {      
      console.log('UPDATING GEOMETRY')
      store.map.updateGeometry(props.route.geometry, props.liveState)
    }, { deep: true })

    watch(() => props.liveState?.driving, () => {            
      store.map.updateGeometry(props.route.geometry, props.liveState)
    })

    function zoom(change: number) {
      store.map.zoom(change)
    }

    onUnmounted(() => {
      store.map.terminate()
    })
    
    return { loading, zoom }
  }
})
</script>

<style lang="scss">
#map-loader {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 10000;
  display: flex;
  justify-content: center;
  align-items: center;
}
#map, #map-wrapper {
  width: 100%;
  height: 100%;
  background: var(--color-card);
  overflow: hidden;

  .driver, .estimate {
    z-index: 1000;
    height: 1.5rem;
    width: 1.5rem;
    position: relative;
    background: var(--color-error);
    border-radius: 1rem;
    border: .25rem solid white;
    box-shadow: var(--card-shadow);
    &:before {
      content:"";
      border-radius: 1rem;
      animation: driver 2s infinite;
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      right: 0;
      background: var(--color-error);
      z-index: -1;
    }
  }

  .estimate {
    background: var(--color-error);
    &:before {
      background: var(--color-error);
    }
  }

  .marker {
    display: block;
    border: none;
    cursor: pointer;
    padding: 0;
    margin-top: -11px;
    margin-left: -6px;
    background: url('../../assets/marker.svg') center top no-repeat;
    background-size: 100% auto;

    &.coming {
      opacity: 0.5;
    }
    &.current {
      transform: scale(1.5);
    }

    &:hover:before {
      opacity: 1;
      transform: translateX(2rem);
    }

    &:after {
        font-family: 'Poppins';
        content: attr(data-symbol);
        color: var(--color-on-primary);
        position: absolute;
        top: 0;
        right: 0;
        left: 0;
        text-align: center;
        line-height: 28px;
        transform: scale(1.25);
        font-weight: bold;
    }

    &.firstlast {
        width: 37.5px !important;
        height: 60px !important;
        z-index: 1;
        margin-left: 0;
        margin-top: -16px;
        &:after {
            font-size: 19px;
            line-height: 35px;
        }
    }
  }
}

#map-live {
  position: fixed;
  top: .5rem;
  left: calc(var(--margin-left) + 26.5rem);
  z-index: 1000;
}

#map-controls {
  position: fixed;
  right: 1rem;
  bottom: 5rem;
  padding: .5rem;
  display: flex;
  flex-direction: column;
  border-radius: 1.5rem;
}


@keyframes driver {
  0% {transform: scale(1); opacity: .75;}
  50%, 100% {transform: scale(5); opacity: 0;}
}

</style>